import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ROUTES } from "@shared/constants";
import { trackPageView } from "@services/analytics/analytics";
import { trackPixelPageView } from "@services/analytics/metaPixel";
import { MainLayout } from "@components/templates/MainLayout";
import { HomePage } from "@modules/events/pages/HomePage";
import { EventDetailPage } from "@modules/events/pages/EventDetailPage";
import { ProfilePage } from "@modules/auth/pages/ProfilePage";
import { AboutPage } from "@modules/contact/pages/AboutPage";
import { ContactPage } from "@modules/contact/pages/ContactPage";
import { FAQPage } from "@modules/faq/pages/FAQPage";
import { MyTicketsPage } from "@modules/tickets/pages/MyTicketsPage";
import { PaymentReturnPage, PaymentCancelPage } from "@modules/payments/pages";
import { StaticContentPage } from "@modules/legal/pages/StaticContentPage";
import { ProtectedRoute } from "@components/organisms/ProtectedRoute";

/**
 * Redirects /sale/confirm?id=X&clientTransactionId=Y → /payment/return?id=X&clientTransactionId=Y
 * Needed because PayPhone dashboard still has the old web_ticketin URL configured.
 */
function SaleConfirmRedirect() {
  const location = useLocation();
  return <Navigate to={`${ROUTES.PAYMENT_RETURN}${location.search}`} replace />;
}

/**
 * Redirects /event/:slug → /events/:slug
 * Links shared publicly use the singular form from the old site.
 */
function EventDetailRedirect() {
  const location = useLocation();
  return <Navigate to={`/events${location.pathname.replace(/^\/event/, "")}`} replace />;
}

/**
 * Fires a GA4 page_view and a Meta Pixel PageView on every route change.
 *
 * Both are needed: this is a SPA, so the pixel's initial PageView only covers the first screen.
 */
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
    trackPixelPageView();
  }, [location.pathname]);
  return null;
}

/**
 * Application router.
 * All public and protected routes are declared here.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route element={<><AnalyticsTracker /><MainLayout /></>}>
        {/* Public routes */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.EVENT_DETAIL} element={<EventDetailPage />} />
        <Route path={ROUTES.ABOUT} element={<AboutPage />} />
        <Route path={ROUTES.CONTACT} element={<ContactPage />} />
        <Route path={ROUTES.FAQ} element={<FAQPage />} />

        {/* Legal / static CMS pages */}
        <Route
          path={ROUTES.TERMS}
          element={<StaticContentPage slug="terminos" title="Términos y Condiciones" />}
        />
        <Route
          path={ROUTES.PRIVACY}
          element={<StaticContentPage slug="privacidad" title="Protección de Datos Personales" />}
        />

        {/* Payment callback routes */}
        <Route path={ROUTES.PAYMENT_RETURN} element={<PaymentReturnPage />} />
        <Route path={ROUTES.PAYMENT_CANCEL} element={<PaymentCancelPage />} />
        {/* Legacy redirect — PayPhone dashboard still points to /sale/confirm (web_ticketin URL) */}
        <Route path="/sale/confirm" element={<SaleConfirmRedirect />} />
        {/* Legacy redirects — web_ticketin URL compatibility */}
        <Route path="/event/:slug" element={<EventDetailRedirect />} />
        <Route path="/events" element={<Navigate to={ROUTES.HOME} replace />} />
        <Route path="/myevents" element={<Navigate to={ROUTES.MY_TICKETS} replace />} />
        <Route path="/mispagos" element={<Navigate to={ROUTES.MY_TICKETS} replace />} />
        <Route path="/nosotros" element={<Navigate to={ROUTES.ABOUT} replace />} />
        <Route path="/politica" element={<Navigate to={ROUTES.PRIVACY} replace />} />
        <Route path="/sale/payment" element={<Navigate to={ROUTES.PAYMENT_RETURN} replace />} />
        <Route path="/sale/resume" element={<Navigate to={ROUTES.PAYMENT_RETURN} replace />} />
        <Route path="/app/confirm" element={<Navigate to={ROUTES.PAYMENT_RETURN} replace />} />
        <Route path="/notify" element={<Navigate to={ROUTES.HOME} replace />} />
        <Route path="/auth/forget-password" element={<Navigate to={ROUTES.HOME} replace />} />

        {/* Protected routes — require authentication */}
        <Route
          path={ROUTES.MY_TICKETS}
          element={
            <ProtectedRoute>
              <MyTicketsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}
